'use client'

import React, { useEffect } from 'react'

import { useSmsSubscriptionApi } from '@/app/users/api/useSmsSubscriptionApi'
import { useSmsMarketingEnabled } from '@/hooks/useSmsMarketingEnabled'
import { setSmsSubscriptionState } from '@/utils/smsSubscriptionState'

/**
 * Sole publisher of the signed-in user's marketing SMS subscription state to
 * the module singleton in smsSubscriptionState.ts (GSE-12330). Mounted exactly
 * once in AppProviders beside SmsMarketingConfigBootstrap; renders nothing.
 *
 * Reads the existing `user { smsSubscription }` query. Its key carries the
 * user id and the app's QueryClient sets no staleTime, so it refetches on
 * every mount — every logged-in page load. That read is also where the backend
 * ties an opt-in made while logged out to the account, matching on the
 * account's phone number, so this bootstrap is what makes that happen on a
 * fresh login as well as what hides the modals afterwards. Gated on the brand
 * flag like the other subscription readers: while SMS marketing is off for the
 * brand nothing is fetched and the state publishes as null.
 */
const SmsSubscriptionStateBootstrap: React.FC = () => {
  const { enabled } = useSmsMarketingEnabled()
  const { data, isSuccess } = useSmsSubscriptionApi(enabled)

  useEffect(() => {
    setSmsSubscriptionState(isSuccess ? data?.state ?? null : null)
  }, [data, isSuccess])

  return null
}

export default SmsSubscriptionStateBootstrap
